import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowUp, ArrowDown, Equals, ArrowsClockwise, Bank, Info
} from "@phosphor-icons/react";
import CpiChart from "@/components/CpiChart";

function ChangeIndicator({ value, suffix = "" }) {
  if (value === null || value === undefined) return <span className="text-zinc-600 text-xs font-data">--</span>;
  const isPositive = value > 0;
  const isZero = value === 0;
  // Higher rates = red (hawkish/tight), lower rates = green (dovish/loose) — matches markets framing
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-data font-medium ${isZero ? "text-zinc-400" : isPositive ? "text-red-400" : "text-emerald-400"}`}>
      {isPositive ? <ArrowUp size={10} weight="bold" /> : isZero ? <Equals size={10} /> : <ArrowDown size={10} weight="bold" />}
      {isPositive ? "+" : ""}{value?.toFixed?.(3) ?? value}{suffix}
    </span>
  );
}

export default function FedRateReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartView, setChartView] = useState("1y");

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await axios.get(`${API}/fed-rate`);
      setData(resp.data);
    } catch (e) {
      console.error("Failed to load Fed Rate:", e);
      setError(e.response?.data?.detail || "Failed to load Fed Rate data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const current = data?.current || {};
  const rate = current.rate_pct;
  const rateColor = rate >= 5 ? "#ef4444" : rate >= 3 ? "#f59e0b" : "#10b981";
  const chartData = chartView === "5y" ? (data?.chart_5y || []) : (data?.chart_1y || []);

  return (
    <div className="p-4 sm:p-5 space-y-5 max-w-[1600px] overflow-x-hidden" data-testid="fed-rate-report">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="min-w-0">
          <h1 className="font-heading text-xl sm:text-2xl font-bold text-zinc-50 tracking-tight">Federal Funds Rate</h1>
          <p className="text-xs text-zinc-500 mt-1 flex items-center gap-1.5 flex-wrap">
            <Bank size={12} />
            Daily Effective Rate · Source: FRED (St. Louis Fed)
          </p>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          data-testid="fedrate-refresh-btn"
          className="flex items-center gap-1.5 text-[10px] text-zinc-400 hover:text-zinc-200 transition-colors px-2 py-1 border border-zinc-800 rounded-sm"
        >
          <ArrowsClockwise size={12} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-sm p-4 text-center" data-testid="fedrate-error">
          <p className="text-xs text-red-400">{error}</p>
          <button onClick={fetchData} className="text-[10px] text-red-300 underline mt-1">Try Again</button>
        </div>
      )}

      {loading && (
        <div className="space-y-4">
          <Skeleton className="h-[140px] bg-zinc-900/50" />
          <Skeleton className="h-[250px] bg-zinc-900/50" />
        </div>
      )}

      {!loading && !error && data && (
        <>
          {/* Hero stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="fedrate-stats">
            <div className="bg-zinc-900/50 border border-amber-500/25 rounded-sm p-4 col-span-2 md:col-span-1">
              <p className="text-[9px] uppercase tracking-wider text-zinc-500 mb-1">Current Rate</p>
              <p className="text-3xl font-data font-bold" style={{ color: rateColor }}>{rate?.toFixed(2)}%</p>
              <p className="text-[10px] text-zinc-600 mt-1">{current.date}</p>
            </div>
            <div className="bg-zinc-900/40 border border-amber-500/20 rounded-sm p-4">
              <p className="text-[9px] uppercase tracking-wider text-zinc-500 mb-1">Day Change</p>
              <ChangeIndicator value={current.day_change} suffix=" pp" />
              <p className="text-[9px] text-zinc-600 mt-1">Prev: {current.prev_close?.toFixed(2)}%</p>
            </div>
            <div className="bg-zinc-900/40 border border-amber-500/20 rounded-sm p-4">
              <p className="text-[9px] uppercase tracking-wider text-zinc-500 mb-1">YoY Change</p>
              <ChangeIndicator value={current.yoy_change} suffix=" pp" />
              <p className="text-[9px] text-zinc-600 mt-1">1Y ago: {current.year_ago?.toFixed(2)}%</p>
            </div>
            <div className="bg-zinc-900/40 border border-amber-500/20 rounded-sm p-4">
              <p className="text-[9px] uppercase tracking-wider text-zinc-500 mb-1">1Y High</p>
              <p className="text-sm font-data font-bold text-red-400">{data.window_1y?.high?.toFixed(2)}%</p>
              <p className="text-[9px] text-zinc-600 mt-1">{data.window_1y?.high_date}</p>
            </div>
            <div className="bg-zinc-900/40 border border-amber-500/20 rounded-sm p-4">
              <p className="text-[9px] uppercase tracking-wider text-zinc-500 mb-1">1Y Low</p>
              <p className="text-sm font-data font-bold text-emerald-400">{data.window_1y?.low?.toFixed(2)}%</p>
              <p className="text-[9px] text-zinc-600 mt-1">{data.window_1y?.low_date}</p>
            </div>
          </div>

          {/* Chart */}
          <div className="bg-zinc-900/40 border border-amber-500/20 rounded-sm p-4" data-testid="fedrate-chart">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <div>
                <p className="text-xs font-medium text-zinc-200">Rate History</p>
                <p className="text-[10px] text-zinc-500">Daily effective rate — where interest rates trend over time</p>
              </div>
              <Tabs value={chartView} onValueChange={setChartView}>
                <TabsList className="h-7 bg-zinc-950 border border-amber-500/30 p-0.5">
                  <TabsTrigger value="1y" className="text-[10px] px-2 h-6 data-[state=active]:bg-amber-500/15 data-[state=active]:text-amber-300" data-testid="fedrate-range-1y">1Y</TabsTrigger>
                  <TabsTrigger value="5y" className="text-[10px] px-2 h-6 data-[state=active]:bg-amber-500/15 data-[state=active]:text-amber-300" data-testid="fedrate-range-5y">5Y</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            {chartData.length > 0 ? (
              <CpiChart
                data={chartData}
                color="#f59e0b"
                height={300}
              />
            ) : (
              <div className="h-[300px] flex items-center justify-center text-xs text-zinc-500">No chart data</div>
            )}
          </div>

          {/* Milestones */}
          {data.milestones && data.milestones.length > 0 && (
            <div className="bg-zinc-900/40 border border-amber-500/20 rounded-sm p-4" data-testid="fedrate-milestones">
              <p className="text-xs font-medium text-zinc-200 mb-1">Recent Rate Pivots</p>
              <p className="text-[10px] text-zinc-500 mb-3">Moments where the rate shifted by 25 basis points (0.25%) or more</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                {data.milestones.map((m) => {
                  const up = (m.change ?? 0) > 0;
                  const tone = up ? "text-red-400 border-red-500/30 bg-red-500/5" : "text-emerald-400 border-emerald-500/30 bg-emerald-500/5";
                  return (
                    <div key={`${m.date}-${m.value}`} className={`rounded-sm border p-2.5 ${tone}`}>
                      <p className="text-[9px] text-zinc-500">{m.date}</p>
                      <p className="text-sm font-data font-bold mt-0.5">{m.value?.toFixed(2)}%</p>
                      <p className="text-[9px] mt-0.5 font-data">
                        {up ? "▲" : "▼"} {Math.abs(m.change).toFixed(2)} pp
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* What is it? */}
          <div className="bg-zinc-900/40 border border-blue-500/25 rounded-sm p-4" data-testid="fedrate-explainer">
            <div className="flex items-start gap-2 mb-2">
              <Info size={14} weight="bold" className="text-blue-400 mt-0.5" />
              <p className="text-sm font-medium text-blue-200">What is the Federal Funds Rate?</p>
            </div>
            <p className="text-xs text-zinc-400 leading-relaxed mb-3">
              The rate banks charge each other for overnight loans. It's the headline lever the Federal Reserve uses to steer the US economy — the single most watched number in global finance.
              Changes ripple into mortgages, credit cards, business loans, and eventually stock &amp; crypto prices.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div className="p-2.5 rounded-sm bg-zinc-950/60 border border-zinc-800">
                <p className="text-[10px] font-medium text-zinc-200 mb-1 flex items-center gap-1"><ArrowUp size={10} className="text-red-400" /> When the rate goes up…</p>
                <p className="text-[10px] text-zinc-500 leading-relaxed">Borrowing gets expensive. Stocks &amp; crypto tend to face headwinds. Savings accounts pay more. The Fed is usually fighting inflation.</p>
              </div>
              <div className="p-2.5 rounded-sm bg-zinc-950/60 border border-zinc-800">
                <p className="text-[10px] font-medium text-zinc-200 mb-1 flex items-center gap-1"><ArrowDown size={10} className="text-emerald-400" /> When the rate goes down…</p>
                <p className="text-[10px] text-zinc-500 leading-relaxed">Borrowing gets cheap. Risk assets (stocks, crypto) usually rally. The Fed is usually trying to stimulate growth or fight a slowdown.</p>
              </div>
            </div>
            <p className="text-[10px] text-zinc-600 mt-3">
              Source: <a href={data.source_url} target="_blank" rel="noreferrer" className="text-amber-400 hover:underline">{data.source}</a>
            </p>
          </div>
        </>
      )}
    </div>
  );
}
